import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import type { SavedFilterDefinition } from '@/services';
import {
    getAdvancedFiltersCounts,
    isJsonLogicRule,
    parseAdvancedFilterDefinition,
    parseAdvancedFilterDefinitions,
} from '@/shared/components/filters';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export const isListFilterRule = (value: unknown): value is ListFilterRule =>
    isRecord(value) &&
    typeof value.field === 'string' &&
    typeof value.fieldLabel === 'string' &&
    typeof value.id === 'string' &&
    typeof value.operator === 'string' &&
    typeof value.operatorLabel === 'string' &&
    'value' in value;

export const normalizeWorkspaceResourceFilterDefinition = (value: unknown): SavedFilterDefinition<ListFilterRule> => {
    const recordValue = isRecord(value) ? value : undefined;
    const ruleCandidates = Array.isArray(value) ? value : Array.isArray(recordValue?.rules) ? recordValue.rules : [];
    const nestedJsonLogic = recordValue?.jsonLogic;
    const legacyJsonLogic =
        recordValue && !('rules' in recordValue) && !('jsonLogic' in recordValue) ? recordValue : undefined;

    const advancedFilters = parseAdvancedFilterDefinitions(recordValue?.advancedFilters, isListFilterRule);
    const legacyAdvancedFilter = parseAdvancedFilterDefinition(recordValue?.advancedFilter, isListFilterRule);
    const normalizedAdvancedFilters = advancedFilters.length
        ? advancedFilters
        : legacyAdvancedFilter
          ? [legacyAdvancedFilter]
          : [];

    return {
        ...(normalizedAdvancedFilters.length ? { advancedFilters: normalizedAdvancedFilters } : {}),
        jsonLogic: isJsonLogicRule(nestedJsonLogic)
            ? nestedJsonLogic
            : isJsonLogicRule(legacyJsonLogic)
              ? legacyJsonLogic
              : undefined,
        rules: ruleCandidates.filter(isListFilterRule),
    };
};

export const hasWorkspaceResourceFilterDefinition = (definition: SavedFilterDefinition<ListFilterRule>) =>
    (Array.isArray(definition.rules) && definition.rules.length > 0) ||
    getAdvancedFiltersCounts(
        definition.advancedFilters ?? (definition.advancedFilter ? [definition.advancedFilter] : []),
    ).conditions > 0 ||
    Boolean(definition.jsonLogic && Object.keys(definition.jsonLogic).length > 0);
